import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';

/**
 * The one tab bar. Replaces nine independent implementations (P2-03).
 *
 * `level` encodes HIERARCHY, which is the point: a user should always be able to
 * tell whether they are switching a whole page or just the contents of a panel.
 * The nine bars this replaces varied in padding, gap, active treatment and
 * accessibility, so the same interaction looked like a different control
 * depending on where you met it.
 *
 * What this owns that none of the nine did completely:
 *
 * - Real tab semantics: role="tablist" / role="tab" / aria-selected /
 *   aria-controls, plus a roving tabindex and Left/Right/Home/End keys. Some of
 *   the originals were bare <button>s with no roles at all.
 * - A weight change (500 -> 600) on the active tab, so state survives for anyone
 *   who cannot distinguish the hue.
 * - Overflow as a priority menu, not a scroller. The workspace bar used
 *   `overflow-x-hidden` with gradient-masked arrows — no scrollbar, so hidden
 *   tabs were genuinely invisible, and its tab set already varies by permission
 *   and feature flag.
 */

/**
 * Structural rather than `LucideIcon`, because <Tabs> only ever passes these two
 * props — and several call sites type their icons as a plain ComponentType.
 * Lucide icons satisfy this, so nothing has to be cast.
 */
export type TabIcon = React.ComponentType<{ className?: string }>;

export interface TabItem {
  id: string;
  label: string;
  icon?: TabIcon;
  /** Rendered after the label — counts, unread dots. */
  badge?: React.ReactNode;
  disabled?: boolean;
}

export interface TabsProps {
  /**
   * `section` switches the whole page (workspace, bill detail, admin sections).
   * `panel` switches content in place (report builder, CF explorer, widgets).
   */
  level?: 'section' | 'panel';
  tabs: TabItem[];
  activeId: string;
  onChange: (id: string) => void;
  /**
   * `menu` collapses tabs that do not fit into a "More" menu. `none` lets them
   * wrap, which is right for short sets in narrow panels.
   */
  overflow?: 'menu' | 'none';
  /** Accessible name for the tablist — always provide one when the bar has no visible heading. */
  ariaLabel?: string;
  className?: string;
  /** Rendered right-aligned on the same rail (filters, a count, an action). */
  actions?: React.ReactNode;
}

const LEVEL_STYLES = {
  section: {
    // gapClass is the class; gap is the same value as a number, which measurement
    // needs. Change both together.
    gapClass: 'gap-2',
    gap: 8,
    list: 'flex items-end gap-2 border-b border-border-light dark:border-border-dark -mb-px',
    // Folder tabs: the active one joins the rail below it.
    tab: 'flex items-center gap-2 rounded-t-[10px] px-[18px] py-[11px] text-sm whitespace-nowrap transition-colors',
    /**
     * Light mode fills with the brand purple. Dark mode CANNOT: the fill was
     * `primary-dark-100` (#3E2750), which measures 1.44:1 against the #121212
     * page — the tab's shape was effectively invisible, and only its white label
     * showed. Brand purple is a dark fill; it needs a light ground to be a
     * surface at all.
     *
     * So in dark the elevation is carried by the neutral ramp (#2D2D2D) and the
     * brand identity moves into the label, which is the surface rule's "brand
     * purple stops being a surface" in practice.
     *
     * The label is `primary-dark-500` (#B594C8), not the `primary-dark-400`
     * (#A07BB8) used at panel level. On a raised #2D2D2D fill, #A07BB8 measures
     * 3.93:1 — under AA for 14px text. #B594C8 reaches 5.27:1. Panel-level tabs
     * keep -400 because they sit on the page/card, where it measures 5.35:1.
     */
    active:
      'bg-primary-800 text-white dark:bg-background-dark-tertiary dark:text-primary-dark-500 font-semibold',
    idle:
      'bg-transparent text-text-secondary dark:text-text-dark-secondary font-medium hover:bg-background-tertiary dark:hover:bg-background-dark-tertiary hover:text-text-primary dark:hover:text-text-dark-primary',
  },
  panel: {
    gapClass: 'gap-7',
    gap: 28,
    list: 'flex items-center gap-7 border-b border-border-light dark:border-border-dark',
    // `border-solid` is REQUIRED, not decorative. Ionic's normalize (loaded
    // globally from main.tsx) has `button { border: 0 }`, and that element
    // selector outranks Tailwind preflight's `*, ::before, ::after` rule which is
    // where `border-style: solid` comes from. Tailwind splits width and style, so
    // `border-b-2` sets only the width — with style `none`, CSS forces the used
    // width to 0 and the underline never paints. Verified on dev: the active tab
    // computed `border-bottom: none 0px`, so this bar had no underline at all
    // (the previous implementation had the same bug, on the same <button>).
    tab: 'flex items-center gap-2 border-b-2 border-solid px-[2px] py-[13px] text-sm whitespace-nowrap transition-colors -mb-px',
    // primary-dark-400 (#A07BB8), not primary-400 — the latter measured ~4.3:1
    // on the dark surface, below AA for 14px labels. See P1-03.
    //
    // The underline is `border-current` rather than `border-primary-800
    // dark:border-primary-dark-400`, which would render identically. Two reasons:
    // it cannot drift from the label colour, and it keeps the class string clear of
    // `border-primary`, which index.css's mobile block hijacks below 1024px with
    // `.dark button[class*="border-primary"] { color: <orange> !important }`.
    // Verified: with those classes the active tab's label went orange in dark mode
    // on a narrow browser — and phone browsers get this UI, not the Ionic shell.
    active: 'border-current text-primary-800 dark:text-primary-dark-400 font-semibold',
    idle:
      'border-transparent text-slate-500 dark:text-text-dark-secondary font-medium hover:text-text-primary dark:hover:text-text-dark-primary hover:border-border-light dark:hover:border-border-dark',
  },
} as const;

/** Stable id so aria-controls can point at the panel a tab governs. */
export const tabPanelId = (tabId: string) => `${tabId}-panel`;

/**
 * Filled when active, outline when idle — the same treatment as
 * `web/src/components/StatefulIcon`, inlined because `shared/` cannot import
 * from `web/`. This is a third state channel alongside colour and font weight.
 */
const iconState = (active: boolean) =>
  active ? 'fill-current stroke-[1.5]' : 'fill-none stroke-2';

/**
 * How many LEADING tabs fit, given that the active tab is always shown and the
 * "More" button costs width whenever anything is collapsed.
 *
 * Exported and pure so it can be unit-tested: jsdom does no layout, so the only
 * way to cover this in tests is to feed it widths directly. It is worth covering —
 * an earlier version pinned the active tab by extending the leading range to
 * `activeIndex + 1`, which silently defeated collapsing whenever the active tab
 * was near the end of the list. With the last tab selected, every tab was pinned
 * visible and the row overflowed into a page-wide horizontal scrollbar.
 */
export function computeLeadingCount({
  widths,
  moreWidth,
  available,
  gap,
  activeIndex,
}: {
  widths: number[];
  moreWidth: number;
  available: number;
  gap: number;
  activeIndex: number;
}): number {
  const fits = (n: number) => {
    const shown = widths.slice(0, n);
    if (activeIndex >= n && activeIndex < widths.length) shown.push(widths[activeIndex]);
    if (shown.length === 0) return true;
    let width = shown.reduce((sum, w) => sum + w + gap, -gap);
    if (shown.length < widths.length) width += gap + moreWidth;
    return width <= available;
  };

  for (let n = widths.length; n >= 0; n -= 1) {
    if (fits(n)) return Math.max(1, n);
  }
  // Never collapse to nothing: one tab is always better than a bare menu.
  return 1;
}

const Tabs: React.FC<TabsProps> = ({
  level = 'panel',
  tabs,
  activeId,
  onChange,
  overflow = 'menu',
  ariaLabel,
  className = '',
  actions,
}) => {
  const styles = LEVEL_STYLES[level];
  const wrapRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  /**
   * How many LEADING tabs are shown. The active tab is always shown too, appended
   * after them if it falls outside that range — see `visible` below.
   */
  const [leadingCount, setLeadingCount] = useState(tabs.length);
  const [menuOpen, setMenuOpen] = useState(false);

  /**
   * Measure how many tabs fit, and collapse the rest into the menu.
   *
   * Widths come from a hidden row that always renders EVERY tab, never from the
   * visible row. Measuring the visible row would make collapsing one-way: once a
   * tab moved into the menu it would no longer be measured, so widening the
   * window could never bring it back.
   *
   * The hidden copies are rendered in the ACTIVE style, which is the widest
   * (font-weight 600). Measuring the narrower idle state instead would let a tab
   * fit at 500 and overflow the moment it became active — an oscillation between
   * two layouts, one per render.
   *
   * Widths are read from live layout rather than estimated from label length:
   * icons, badges and per-workspace custom labels make them unpredictable.
   */
  const measure = useCallback(() => {
    if (overflow !== 'menu') {
      setLeadingCount(tabs.length);
      return;
    }
    const wrap = wrapRef.current;
    const row = measureRef.current;
    if (!wrap || !row) return;

    const widths = Array.from(row.querySelectorAll<HTMLElement>('[data-measure-tab]')).map(
      (el) => el.offsetWidth
    );
    const moreWidth =
      row.querySelector<HTMLElement>('[data-measure-more]')?.offsetWidth ?? 96;
    const available = wrap.clientWidth;
    const { gap } = styles;
    const activeIndex = tabs.findIndex((t) => t.id === activeId);

    // Nothing has been laid out yet — a hidden ancestor, a not-yet-rendered
    // parent, or the first pass before styles apply. Collapsing on unmeasurable
    // widths would hide every tab but one and never recover, so show them all and
    // wait for the ResizeObserver to fire with real numbers.
    if (available <= 0 || widths.every((w) => w === 0)) {
      setLeadingCount(tabs.length);
      return;
    }

    setLeadingCount(computeLeadingCount({ widths, moreWidth, available, gap, activeIndex }));
  }, [overflow, tabs, activeId, styles]);

  useLayoutEffect(measure, [measure]);

  useEffect(() => {
    if (overflow !== 'menu') return;
    const observer = new ResizeObserver(measure);

    // The wrapper catches window resize, zoom and sidebar collapse.
    if (wrapRef.current) observer.observe(wrapRef.current);

    // The probe catches everything that changes how WIDE the tabs are without
    // changing the space available to them — which the wrapper cannot see, since
    // it is `flex-1` and keeps its width regardless of content.
    //
    // The webfont is the case that bit us: Inter and Poppins load asynchronously
    // from Google Fonts, so the first measurement runs on fallback metrics, the
    // text reflows wider when Inter arrives, and nothing ever re-measures. The
    // tabs then overflowed into a horizontal scrollbar on the page and only
    // corrected themselves if you happened to resize or zoom.
    if (measureRef.current) observer.observe(measureRef.current);

    return () => observer.disconnect();
  }, [measure, overflow]);

  // Belt to the ResizeObserver's braces: some engines settle font metrics without
  // emitting a resize for the probe.
  useEffect(() => {
    if (overflow !== 'menu' || !document.fonts?.ready) return;
    let cancelled = false;
    document.fonts.ready.then(() => {
      if (!cancelled) measure();
    });
    return () => {
      cancelled = true;
    };
  }, [measure, overflow]);

  useEffect(() => {
    if (!menuOpen) return;
    const close = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) setMenuOpen(false);
    };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, [menuOpen]);

  /**
   * The collapsed row is `[leading tabs] [active tab, if it wasn't among them]`,
   * with everything else in the menu. Pinning the active tab by APPENDING it —
   * rather than by extending the leading range to reach it — is what keeps a
   * late-selected tab from dragging every tab before it onto the row.
   */
  const { visible, hidden } = React.useMemo(() => {
    if (overflow !== 'menu' || leadingCount >= tabs.length) return { visible: tabs, hidden: [] };

    const activeIndex = tabs.findIndex((t) => t.id === activeId);
    const shown = tabs.slice(0, leadingCount);
    if (activeIndex >= leadingCount) shown.push(tabs[activeIndex]);

    const shownIds = new Set(shown.map((t) => t.id));
    return { visible: shown, hidden: tabs.filter((t) => !shownIds.has(t.id)) };
  }, [overflow, tabs, activeId, leadingCount]);

  /** Arrow keys move between tabs; the roving tabindex keeps Tab itself moving on. */
  const onKeyDown = (event: React.KeyboardEvent) => {
    const enabled = tabs.filter((t) => !t.disabled);
    if (enabled.length === 0) return;
    const current = enabled.findIndex((t) => t.id === activeId);

    let next = -1;
    if (event.key === 'ArrowRight') next = (current + 1) % enabled.length;
    else if (event.key === 'ArrowLeft') next = (current - 1 + enabled.length) % enabled.length;
    else if (event.key === 'Home') next = 0;
    else if (event.key === 'End') next = enabled.length - 1;
    else return;

    event.preventDefault();
    onChange(enabled[next].id);
  };

  const renderTab = (tab: TabItem) => {
    const isActive = tab.id === activeId;
    const Icon = tab.icon;
    return (
      <button
        key={tab.id}
        data-tab
        role="tab"
        type="button"
        id={`${tab.id}-tab`}
        aria-selected={isActive}
        aria-controls={tabPanelId(tab.id)}
        // Roving tabindex: only the active tab is in the tab order, so keyboard
        // users move between tabs with arrows rather than tabbing through all nine.
        tabIndex={isActive ? 0 : -1}
        disabled={tab.disabled}
        onClick={() => onChange(tab.id)}
        className={`${styles.tab} ${isActive ? styles.active : styles.idle} ${
          tab.disabled ? 'opacity-50 cursor-not-allowed' : ''
        } focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400 focus-visible:ring-offset-2`}
      >
        {Icon && (
          <span aria-hidden="true" className="flex shrink-0">
            <Icon className={`h-4 w-4 ${iconState(isActive)}`} />
          </span>
        )}
        <span>{tab.label}</span>
        {tab.badge}
      </button>
    );
  };

  return (
    <div className={`flex items-end justify-between gap-3 ${className}`}>
      <div ref={wrapRef} className="relative min-w-0 flex-1">
        {/*
          Hidden width probe. Renders every tab (plus the "More" button) at its
          natural width in the active style so `measure` can decide what fits.
          Deliberately not `display: none` — that reports zero width.

          The 0x0 `overflow: hidden` box around it is load-bearing. `visibility:
          hidden` alone does NOT remove an element from its ancestors' scrollable
          overflow area, so this row — which is deliberately wider than the
          container — made Layout.tsx's <main> (overflow-y-auto, which per spec
          computes overflow-x to auto) grow a horizontal scrollbar across the whole
          page, even with the tabs correctly collapsed. Clipping stops the overflow
          from propagating while leaving the children's offsetWidth intact, because
          clipping is a paint operation and measurement reads layout.

          The clip is an inline style rather than a class on purpose: index.css's
          mobile block matches `[class*="overflow-hidden"]` and forces a border and
          box-shadow onto anything so named.
        */}
        {overflow === 'menu' && (
          <div aria-hidden style={{ position: 'absolute', width: 0, height: 0, overflow: 'hidden' }}>
            <div ref={measureRef} className={`pointer-events-none flex w-max ${styles.gapClass}`}>
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <span key={tab.id} data-measure-tab className={`${styles.tab} font-semibold`}>
                    {Icon && <Icon className="h-4 w-4 shrink-0" />}
                    <span>{tab.label}</span>
                    {tab.badge}
                  </span>
                );
              })}
              <span data-measure-more className={`${styles.tab} font-semibold`}>
                <span>More</span>
                <span className="text-xs tabular-nums">({tabs.length})</span>
                <ChevronDown className="h-4 w-4" />
              </span>
            </div>
          </div>
        )}

        <div
          role="tablist"
          aria-label={ariaLabel}
          aria-orientation="horizontal"
          onKeyDown={onKeyDown}
          className={`${styles.list} ${overflow === 'none' ? 'flex-wrap' : ''}`}
        >
          {visible.map(renderTab)}

          {hidden.length > 0 && (
            <div ref={menuRef} className="relative">
              <button
                type="button"
                onClick={() => setMenuOpen((open) => !open)}
                aria-expanded={menuOpen}
                aria-haspopup="menu"
                className={`${styles.tab} ${styles.idle} focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-400`}
              >
                <span>More</span>
                <span className="text-xs tabular-nums">({hidden.length})</span>
                <ChevronDown className="h-4 w-4" aria-hidden />
              </button>

              {menuOpen && (
                <div
                  role="menu"
                  className="absolute right-0 top-full z-20 mt-1 min-w-[200px] rounded-lg border border-border-light bg-white py-1 shadow-md dark:border-border-dark dark:bg-background-dark-secondary"
                >
                  {hidden.map((tab) => {
                    const Icon = tab.icon;
                    return (
                      <button
                        key={tab.id}
                        role="menuitem"
                        type="button"
                        disabled={tab.disabled}
                        onClick={() => {
                          setMenuOpen(false);
                          onChange(tab.id);
                        }}
                        className="flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-text-primary hover:bg-background-tertiary disabled:opacity-50 dark:text-text-dark-primary dark:hover:bg-background-dark-tertiary"
                      >
                        {Icon && <Icon className="h-4 w-4 shrink-0" />}
                        <span className="flex-1 truncate">{tab.label}</span>
                        {tab.badge}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {actions && <div className="shrink-0 pb-2">{actions}</div>}
    </div>
  );
};

export default Tabs;
